/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect, useState } from "react";
import {
  CalendarIcon,
  NeedHelp,
  PlusIcon,
  SelectArrow,
  TickIcon,
} from "../../atoms/CustomIcons";
import DashboardTemplate from "../../templates/Dashboard";
import Header from "../../atoms/Header";
import { Button, DatePicker, DatePickerProps, Drawer, Select } from "antd";
import dayjs from "dayjs";
import KanbanBoard from "./Component/KanbanBoard";
import EmptyCard from "../../atoms/EmptyCard";
import TimeWrapper from "./Component/TimeWrapper";
import RoutesMap from "./Component/RoutesMap";
import ScheduleWrapper from "./Component/ScheduleWrapper";
import DateNavigatorLeft from "../../atoms/CustomIcons/DateNavigatorLeft";
import DateNavigatorRight from "../../atoms/CustomIcons/DateNavigatorRight";
import LocationIcon from "../../atoms/CustomIcons/LocationIcon";
import ScheduleIcon from "../../atoms/CustomIcons/ScheduleIcon";
import WeatherWrapper from "./Component/WeatherWrapper";
import { useAppDispatch, useAppSelector } from "../../config/hooks";
import { getScheduleByDay, schedulingFilters } from "./ducks/actions";
import { useNavigate } from "react-router-dom";

interface Visit {
  id: string;
  title: string;
  status: string;
  startTime: string;
  endTime: string;
  address: string;
  contract?: string;
}

interface Crew {
  id: string;
  name: string;
  crew_leader: string;
  hours: string;
  visit?: Visit[] | undefined; // Visit is optional as it can be dragged out
}

interface Manager {
  id: string;
  name: string;
  crew: Crew[];
}

interface ServiceLine {
  id: string;
  name: string;
  managers: Manager[];
}

export default function Scheduling() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = window.location.pathname;
  const pathSegments = location.split("/");
  const companyId = pathSegments[pathSegments.length - 1];
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs().toDate());
  const [serviceLineData, setServiceLineData] = useState<{
    dropdown: { label: string; value: string }[];
    selectedServiceLine: ServiceLine[];
    selectedDropdownValue: string | null;
  }>({
    dropdown: [],
    selectedServiceLine: [],
    selectedDropdownValue: null,
  });
  // const [managerData, setManagerData] = useState<{
  //   dropdown: { label: string; value: string }[];
  //   selectedManager: Manager[];
  //   selectedDropdownValue: string | null;
  // }>({
  //   dropdown: [],
  //   selectedManager: [],
  //   selectedDropdownValue: null,
  // });
  // const [crewData, setCrewData] = useState<Crew[]>();
  const [columnData, setColumnData] = useState<any[]>();
  const [showRoutes, setShowRoutes] = useState(false);
  const [showScheduled, setShowScheduled] = useState(false);
  const [scheduleId, setScheduleId] = useState<string | null>(null);
  const [crewWithData, setCrewWithData] = useState<any[]>([]);
  // const [activeCrew, setActiveCrew] = useState<any>(null);
  const [activeScheduleData, setActiveScheduleData] = useState<any>(null);
  const [allScheduleData, setAllScheduleData] = useState<any>(null);

  const schedulingFiltersData = useAppSelector(
    (state) => state?.schedule?.schedulingFiltersData
  );
  const schedulingData = useAppSelector(
    (state) => state?.schedule?.schedulingData
  );

  // Helper function to deep copy objects
  const deepCopy: any = (obj: any) => {
    if (obj === null || typeof obj !== "object") return obj;
    if (obj instanceof Date) return new Date(obj.getTime());
    if (Array.isArray(obj)) return obj.map(deepCopy);

    const copy: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        copy[key] = deepCopy(obj[key]);
      }
    }
    return copy;
  };

  useEffect(() => {
    const formattedDate = dayjs(selectedDate).format("YYYY-MM-DD");
    dispatch(getScheduleByDay(formattedDate));
  }, [selectedDate, dispatch]);

  useEffect(() => {
    dispatch(schedulingFilters());
  }, [dispatch]);

  const onDateChange: DatePickerProps["onChange"] = (date) => {
    if (date) {
      setSelectedDate(date.toDate());
    }
  };

  const serviceLineChangeHandler = (value: string) => {
    setServiceLineData((prev) => ({
      ...prev,
      selectedDropdownValue: value,
    }));
    if (value) {
      const filteredData = allScheduleData?.filter((sl: any) =>
        sl.service_line_ids.includes(value)
      );
      setActiveScheduleData(filteredData);
    } else {
      const scheduledData = deepCopy(schedulingData?.data ?? []);
      const columnCrewsArray: any[] = [];
      const hasCrewData =
        Array.isArray(scheduledData) && scheduledData.length > 0;

      // Create a deep copy of crews data before processing
      const crewsCopy = deepCopy(schedulingFiltersData?.data?.crews || []);

      crewsCopy
        ?.filter((c: any) =>
          !hasCrewData
            ? true
            : !scheduledData?.some((cd: any) => (cd.crew_id ?? cd.id) === c.id)
        )
        ?.forEach((crew: any) => {
          const crewYard = schedulingFiltersData?.data?.crewYard?.find(
            (cy: any) => cy.id === crew?.crew_yard_id
          ) || {
            id: "",
            name: "",
          };

          columnCrewsArray.push({
            crewMembers: crew?.crewMembers || [],
            crewLeader: crew?.crewLeader || {},
            crew_name: crew?.name,
            crew_leader_name: `${crew?.crewLeader?.first_name ?? ""} ${
              crew?.crewLeader?.last_name ?? ""
            }`,
            total_no_of_crew: crew?.crewMembers?.length || 0,
            total_hours: crew?.crewMembers?.length * 8 || 0,
            id: crew?.id,
            crew_id: crew?.id,
            finish_crew_yard_id: crewYard?.id || "",
            finish_crew_yard_name: crewYard?.name || "",
            starting_crew_yard_name: crewYard?.name || "",
            starting_crew_yard_id: crewYard?.id || "",
            starting_point: "CREW_YARD",
            finish_point: "CREW_YARD",
            service_line_ids:
              crew?.crewServiceLines?.map((sl: any) => sl.service_line_id) ||
              [],
            production_manager_ids: [],
          });
        });

      const newActiveScheduleData = [...scheduledData, ...columnCrewsArray];
      setActiveScheduleData(newActiveScheduleData);
    }
  };

  // const managerChangeHandler = (value: string) => {
  //   setManagerData((prev) => ({
  //     ...prev,
  //     selectedDropdownValue: value,
  //   }));
  //   if (value) {
  //     const filteredData = allScheduleData?.filter((sl: any) =>
  //       sl.production_manager_ids.includes(value)
  //     );
  //     setActiveScheduleData(filteredData);
  //   } else {
  //     const scheduledData = deepCopy(schedulingData?.data ?? []);
  //     const columnCrewsArray: any[] = [];
  //     const hasCrewData =
  //       Array.isArray(scheduledData) && scheduledData.length > 0;

  //     // Create a deep copy of crews data before processing
  //     const crewsCopy = deepCopy(schedulingFiltersData?.data?.crews || []);

  //     crewsCopy
  //       ?.filter((c: any) =>
  //         !hasCrewData
  //           ? true
  //           : !scheduledData?.some((cd: any) => (cd.crew_id ?? cd.id) === c.id)
  //       )
  //       ?.forEach((crew: any) => {
  //         const crewYard = schedulingFiltersData?.data?.crewYard?.find(
  //           (cy: any) => cy.id === crew?.crew_yard_id
  //         ) || {
  //           id: "",
  //           name: "",
  //         };

  //         columnCrewsArray.push({
  //           crew_name: crew?.name,
  //           crew_leader_name: `${crew?.crewLeader?.first_name ?? ""} ${
  //             crew?.crewLeader?.last_name ?? ""
  //           }`,
  //           total_no_of_crew: crew?.crewMembers?.length || 0,
  //           total_hours: crew?.crewMembers?.length * 8 || 0,
  //           id: crew?.id,
  //           finish_crew_yard_id: crewYard?.id || "",
  //           finish_crew_yard_name: crewYard?.name || "",
  //           starting_crew_yard_name: crewYard?.name || "",
  //           starting_crew_yard_id: crewYard?.id || "",
  //           starting_point: "CREW_YARD",
  //           finish_point: "CREW_YARD",
  //           service_line_ids:
  //             crew?.crewServiceLines?.map((sl: any) => sl.service_line_id) ||
  //             [],
  //         });
  //       });

  //     setActiveScheduleData([...scheduledData, ...columnCrewsArray]);
  //   }
  // };

  const onClose = () => {
    setShowScheduled(false);
  };

  useEffect(() => {
    if (schedulingFiltersData?.data?.crews && schedulingData?.data) {
      // Create a deep copy of crews data before processing
      const crewsCopy = deepCopy(schedulingFiltersData?.data?.crews || []);

      const crewsHaveData = crewsCopy
        // ?.filter((crew: any) =>
        //   schedulingData?.data?.some((sd: any) => sd.crew_id === crew.id)
        // )
        ?.sort((a: any, b: any) => {
          const aIndex =
            schedulingData?.data?.findIndex(
              (sd: any) => sd?.crew_id === a?.id
            ) ?? -1;
          const bIndex =
            schedulingData?.data?.findIndex(
              (sd: any) => sd?.crew_id === b?.id
            ) ?? -1;
          return aIndex - bIndex;
        });

      setCrewWithData(crewsHaveData || []);
      console.log("crewsHaveData:", crewsHaveData);
    }
  }, [schedulingFiltersData, schedulingData]);
  console.log("scheduleId:", crewWithData, scheduleId);
  // useEffect(() => {
  //   if (scheduleId) {
  //     setActiveCrew(crewWithData?.find((c) => c?.id === scheduleId));
  //   }
  // }, [scheduleId, crewWithData]);

  useEffect(() => {
    if (schedulingFiltersData?.data?.service_lines) {
      setServiceLineData((prev) => ({
        ...prev,
        selectedDropdownValue:
          schedulingFiltersData?.data?.service_lines?.[0]?.value ?? null,
      }));
    }
  }, [schedulingFiltersData?.data?.service_lines]);

  useEffect(() => {
    if (schedulingData?.data && schedulingFiltersData?.data?.crewYard) {
      const scheduledData = deepCopy(schedulingData?.data || []);
      const columnCrewsArray: any[] = [];
      const hasCrewData =
        Array.isArray(scheduledData) && scheduledData.length > 0;

      // Create a deep copy of crews data before processing
      const crewsCopy = deepCopy(schedulingFiltersData?.data?.crews || []);

      crewsCopy
        ?.filter((c: any) =>
          !hasCrewData
            ? true
            : !scheduledData?.some((cd: any) => (cd.crew_id ?? cd.id) === c.id)
        )
        ?.forEach((crew: any) => {
          const crewYard = schedulingFiltersData?.data?.crewYard?.find(
            (cy: any) => cy.id === crew?.crew_yard_id
          ) || {
            id: "",
            name: "",
          };

          columnCrewsArray.push({
            crewMembers: crew?.crewMembers || [],
            crewLeader: crew?.crewLeader || {},
            crew_name: crew?.name,
            crew_leader_name: `${crew?.crewLeader?.first_name ?? ""} ${
              crew?.crewLeader?.last_name ?? ""
            }`,
            total_no_of_crew: crew?.crewMembers?.length || 0,
            total_hours: crew?.crewMembers?.length * 8 || 0,
            id: crew?.id,
            crew_id: crew?.id,
            finish_crew_yard_id: crewYard?.id || "",
            finish_crew_yard_name: crewYard?.name || "",
            starting_crew_yard_name: crewYard?.name || "",
            starting_crew_yard_id: crewYard?.id || "",
            starting_point: "CREW_YARD",
            finish_point: "CREW_YARD",
            service_line_ids:
              crew?.crewServiceLines?.map((sl: any) => sl.service_line_id) ||
              [],
            production_manager_ids: [],
          });
        });

      const newActiveScheduleData = [...scheduledData, ...columnCrewsArray];
      setActiveScheduleData(newActiveScheduleData);
      setAllScheduleData(deepCopy(newActiveScheduleData));
    }
  }, [schedulingData?.data, schedulingFiltersData?.data]);
  return (
    <DashboardTemplate loading={loading || schedulingFiltersData.loading}>
      <Header
        title="Schedule"
        description=""
        btnText1="Need help?"
        btnIcon1={<NeedHelp />}
      />

      <div className="scheduleWrapper">
        <div className="scheduleHeader">
          <div className="leftHeader">
            <DatePicker
              onChange={onDateChange}
              value={dayjs(selectedDate)}
              format={"ddd, MMM DD"}
              allowClear={false}
              suffixIcon={<CalendarIcon />}
            />
            <div className="dateWrapper">
              <Button
                icon={<DateNavigatorLeft />}
                type="text"
                className="only_icon previous_btn"
                onClick={() =>
                  setSelectedDate(
                    dayjs(selectedDate).subtract(1, "day").toDate()
                  )
                }
              />
              <div className="datePickerWrapper">
                <span className="day">
                  {dayjs(selectedDate).format("ddd")},
                </span>
                <span className="date">
                  {dayjs(selectedDate).format("MMM")}
                </span>
                <span className="date">{dayjs(selectedDate).format("DD")}</span>
              </div>
              <Button
                icon={<DateNavigatorRight />}
                type="text"
                className="only_icon next_btn"
                onClick={() =>
                  setSelectedDate(dayjs(selectedDate).add(1, "day").toDate())
                }
              />
            </div>
            <TimeWrapper />
            <WeatherWrapper />
            <Select
              style={{ width: "190px" }}
              suffixIcon={<SelectArrow />}
              menuItemSelectedIcon={<TickIcon style={{ color: "#00A07D" }} />}
              showSearch
              allowClear
              onChange={serviceLineChangeHandler}
              value={serviceLineData.selectedDropdownValue}
              placeholder="All service lines"
              options={schedulingFiltersData?.data?.service_lines}
            />
            {/* <Select
              style={{ width: "150px" }}
              suffixIcon={<SelectArrow />}
              menuItemSelectedIcon={<TickIcon style={{ color: "#00A07D" }} />}
              showSearch
              allowClear
              onChange={managerChangeHandler}
              value={managerData.selectedDropdownValue}
              placeholder="Managers"
              options={schedulingFiltersData?.data?.managers}
            /> */}

            <div className="filterDownload">
              <Button
                onClick={() => setShowRoutes(!showRoutes)}
                icon={<LocationIcon />}
                style={{ height: "36px", width: "36px", borderRadius: "10px" }}
              />
            </div>
          </div>

          <div className="rightHeader">
            <Button
              htmlType="button"
              className="outlined-btn"
              size="large"
              onClick={() => navigate(`/new-task/${companyId}`)}
            >
              <PlusIcon /> Task
            </Button>
            <Button
              type="primary"
              onClick={() => (window.location.href = `/scheduler/${companyId}`)}
            >
              <ScheduleIcon /> Schedule work
            </Button>
          </div>
        </div>

        <div className="kanbanWrapper">
          {activeScheduleData ? (
            <KanbanBoard
              crewData={activeScheduleData}
              setColumnData={setColumnData}
              setScheduleId={setScheduleId}
              setShowScheduled={setShowScheduled}
              selectedDate={selectedDate}
              crews={schedulingFiltersData?.data?.crews}
              setLoading={setLoading}
              loading={loading}
            />
          ) : (
            <div className="empty_wrap">
              <EmptyCard
                imageHeight={220}
                title="No crews available"
                description="You currently have no crews assigned to work with."
                image="/images/empty-icons/empty-schedule.svg"
                h2Style={{
                  fontSize: "18px",
                  color: "#474C4D",
                  lineHeight: "26px",
                }}
                pStyle={{ color: "#474C4D" }}
              />
            </div>
          )}
          {showRoutes && (
            <div className="routesBlackWrapper">
              <div className="routesWrapper">
                <RoutesMap
                  routeData={columnData?.filter(
                    (col) => col?.visits?.length > 0
                  )}
                  setShowRoutes={setShowRoutes}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <Drawer
        placement="bottom"
        width="100%"
        onClose={onClose}
        open={showScheduled}
        className="drawer-bottom"
        destroyOnHidden={true}
        maskClosable={false}
        mask={false}
        closable={false}
      >
        <ScheduleWrapper
          selectedDate={selectedDate}
          crews={crewWithData}
          activeCrew={crewWithData?.find((c) => c?.id === scheduleId)}
          onClose={() => setShowScheduled(false)}
          setLoading={setLoading}
          loading={loading}
        />
      </Drawer>
    </DashboardTemplate>
  );
}
